import ApplicationImpl from "./application";
import { Glue42 } from "../../glue";
import { AppProps, Configuration, InstanceProps } from "./types";
import { default as CallbackRegistryFactory, CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import InstanceImpl from "./instance";
import { Glue42Core } from "@glue42/core";
import { ApplicationConfig } from "@glue42/schemas";
import { GetApplicationsMethodName } from "./const/agm-names";
import { validate } from "./helper";

export default class AppManagerImpl implements Glue42.AppManager.AppManager {

    private _apps: { [key: string]: ApplicationImpl } = {};
    private _instances: InstanceImpl[] = [];
    private _registry: CallbackRegistry = CallbackRegistryFactory();

    constructor(
        public mode: Glue42.AppManager.Mode,
        private _agm: Glue42Core.AGM.API,
        private _activities: Glue42.Activities.API,
        private _windows: Glue42.Windows.API,
        private _logger: Glue42Core.Logger.API,
        private _gdMajorVersion: number,
        private _configuration: () => Configuration
    ) {
        //
    }

    public getConfigurations = async (apps?: string[]): Promise<ApplicationConfig[]> => {
        const args = {
            v2: {
                apps: undefined as undefined | string[]
            }
        };

        if (Array.isArray(apps)) {
            args.v2 = {
                apps
            };
        }
        const result = await this._agm.invoke(GetApplicationsMethodName, args);
        return result.returned.applications;
    }

    public application = (name: string) => {
        if (this._configuration()?.throwErrors && typeof name !== "string") {
            throw new Error(`"name" must be string`);
        }

        return this._apps[name];
    }

    public applications = () => {
        return Object.keys(this._apps).map((k: string) => this._apps[k]);
    }

    public instances = () => {
        return this._instances.map((i) => i);
    }

    public getMyInstance = () => {
        const glue42gd = typeof window !== "undefined" && (window as any).glue42gd;
        if (glue42gd) {
            if (this._gdMajorVersion >= 3) {
                // GD3 check
                const instanceId = glue42gd.appInstanceId;
                return this._instances.find((i) => i.id === instanceId);
            }
        } else {
            const instanceId = this._agm.instance.instance;
            return this._instances.find((i) => i.id === instanceId);
        }
        return undefined;
    }

    public getMyApplication = () => {
        if (this._agm.instance) {
            return this.application(this._agm.instance.applicationName);
        }
    }

    public handleSnapshotAppsAdded = (newApps: AppProps[]) => {
        const currentApps = this.applications();
        if (currentApps.length > 0) {
            currentApps.forEach((item) => {
                const name = item.name;
                const alreadyExists = newApps.find((i) => i.Name === item.name);
                if (!alreadyExists) {
                    // Check if app is present in glue.js but not in snapshot, remove apps which are no longer present in gd
                    this.handleAppRemoved({ Name: name });
                }
            });
        }
        newApps.forEach((item: AppProps) => {
            const alreadyExists = currentApps.find((i) => i.name === item.Name);
            if (!alreadyExists) {
                // Check if app is already present within glue.js, add only missing ones
                this.handleAppAdded(item);
            }
        });
    }

    public handleSnapshotInstanceStarted = (newInstances: InstanceProps[]) => {
        const currentInstances = this.instances();
        if (currentInstances.length > 0) {
            currentInstances.forEach((item) => {
                const id = item.id;
                const alreadyExists = newInstances.find((i) => i.Id === id);
                if (!alreadyExists) {
                    // Check if the instance is present in glue.js but not in snapshot, remove the instance which are no longer present in gd
                    this.handleInstanceStopped({ Name: item.application.name, Id: id });
                }
            });
        }
        newInstances.forEach((item) => {
            const alreadyExists = currentInstances.find((i) => i.id === item.Id);
            if (!alreadyExists) {
                // Check if the instance is already present within glue.js, add only missing ones
                this.handleInstanceStarted(item);
            }
        });
    }

    public handleAppAdded = (props: AppProps) => {
        const id = this._getAppId(props);
        this._logger.trace(`adding app ${id}`);
        this._apps[id] = new ApplicationImpl(this, id, this._agm, this._configuration);
        const app = this._updateAppFromProps(props);

        this._registry.execute("appAdded", app);
        // keeps backwards compatibility
        this._registry.execute("appAvailable", app);
    }

    public handleAppUpdated = (props: AppProps) => {
        // TODO: check side effects, is this._apps updated?

        const app = this._updateAppFromProps(props);

        this._registry.execute("appChanged", app);
    }

    public handleAppRemoved = (props: AppProps) => {
        const id = this._getAppId(props);
        this._logger.trace(`removing app ${id}`);
        const app = this.application(id);

        // remove all instances related to the app
        this._instances = this._instances.filter((i) => i.application.name !== app.name);

        delete this._apps[id];

        this._registry.execute("appRemoved", app);
    }

    // not called from GD3
    public handleAppReady = (props: AppProps) => {
        const id = this._getAppId(props);
        const app = this._getAppOrThrow(id);
        app.updateFromProps(props);

        if (app.available) {
            this._registry.execute("appAvailable", app);
        } else {
            this._registry.execute("appUnavailable", app);
        }
    }

    public handleInstanceStarted = (props: InstanceProps): InstanceImpl => {
        this._logger.trace(`started app ${props.Name} ${props.Id}`);
        const id = this._getInstanceId(props);
        const appName = this._getInstanceAppName(props);

        const instance = new InstanceImpl(id, appName, this, this._agm, this._activities, this._windows, false, this._configuration);

        this._updateInstanceFromProps(instance, props);

        this._instances.push(instance);

        this._registry.execute("instanceStarted", instance);
        return instance;
    }

    public handleInstanceStopped = (props: Pick<InstanceProps, "Name" | "Id">) => {
        this._logger.trace(`failed to start app ${props.Name} ${props.Id}`);

        const id = this._getInstanceId(props);
        const appName = this._getInstanceAppName(props);
        const instance = this._getInstanceOrThrow(id, appName);

        this._instances = this._instances.filter((i) => !this._matchInstance(i, id, appName));

        this._registry.execute("instanceStopped", instance);
        instance.done();
    }

    public handleInstanceAgmServerReady = (props: InstanceProps) => {

        const id = this._getInstanceId(props);
        const appName = this._getInstanceAppName(props);
        const instance = this._getInstanceOrThrow(id, appName);

        instance.updateAgmInstanceFromProps(props);

        this._registry.execute("instanceAgmServerReady", instance);
    }

    public handleInstanceStartFailed = (props: InstanceProps) => {

        const id = this._getInstanceId(props);
        const appName = this._getInstanceAppName(props);
        const startFailed = true;
        const instance = new InstanceImpl(id, appName, undefined, undefined, undefined, undefined, startFailed, this._configuration);

        this._updateInstanceFromProps(instance, props);

        this._registry.execute("instanceStartFailed", instance);
    }

    public handleInstanceUpdated = (props: InstanceProps) => {

        const id = this._getInstanceId(props);
        const app = this._getInstanceAppName(props);
        const instance = this._getInstanceOrThrow(id, app);

        this._updateInstanceFromProps(instance, props);
    }

    /** Event subscription methods */
    public onInstanceStarted = (callback: (item: Glue42.AppManager.Instance) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("instanceStarted", callback, this._instances);
    }

    public onInstanceStartFailed = (callback: (item: Glue42.AppManager.Instance) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("instanceStartFailed", callback);
    }

    public onInstanceStopped = (callback: (item: Glue42.AppManager.Instance) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("instanceStopped", callback);
    }

    public onInstanceUpdated = (callback: (item: Glue42.AppManager.Instance) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("instanceChanged", callback);
    }

    public onInstanceAgmServerReady = (callback: (item: Glue42.AppManager.Instance) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("instanceAgmServerReady", callback);
    }

    public onAppAdded = (callback: (item: Glue42.AppManager.Application) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("appAdded", callback, Object.values(this._apps));
    }

    public onAppRemoved = (callback: (item: Glue42.AppManager.Application) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("appRemoved", callback);
    }

    public onAppAvailable = (callback: (item: Glue42.AppManager.Application) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("appAvailable", callback);
    }

    public onAppUnavailable = (callback: (item: Glue42.AppManager.Application) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("appUnavailable", callback);
    }

    public onAppChanged = (callback: (item: Glue42.AppManager.Application) => any): UnsubscribeFunction => {
        validate(callback, this._configuration());
        return this._registry.add("appChanged", callback);
    }

    private _getAppOrThrow(id: string) {
        const result = this.application(id);
        if (!result) {
            throw Error(`app with id ${id} not found`);
        }
        return result;
    }

    private _getAppId(props: AppProps) {
        return props.Name;
    }

    private _matchInstance(instance: Glue42.AppManager.Instance, id: string, appName: string) {
        return instance.id === id && instance.application.name === appName;
    }

    private _getInstanceByIdAndName(id: string, appName: string) {
        return this._instances.filter((i) => this._matchInstance(i, id, appName))[0];
    }

    private _getInstanceOrThrow(id: string, appName: string) {
        const result = this._getInstanceByIdAndName(id, appName);
        if (!result) {
            throw Error(`instance with id ${id} not found`);
        }
        return result;
    }

    private _getInstanceId(props: Pick<InstanceProps, "Id">) {
        return props.Id;
    }

    private _getInstanceAppName(props: Pick<InstanceProps, "Name">) {
        return props.Name;
    }

    private _updateAppFromProps(props: AppProps) {
        const id = this._getAppId(props);
        this._logger.trace(`updating app with  + ${id}, ${JSON.stringify(props)}`);
        const app = this._getAppOrThrow(id);

        app.updateFromProps(props);

        return app;
    }

    private _updateInstanceFromProps(instance: InstanceImpl, props: InstanceProps) {
        this._logger.trace("updating instance with " + this._getInstanceId(props) + " for app " + this._getInstanceAppName(props));
        instance.updateFromProps(props);
    }
}
